import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Popconfirm,
  Result,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  ExperimentOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { EllipsisText, formatDateTime, JsonBlock } from '../Assessments/shared';
import { DraftBlueprintBinding } from './actionTypes';
import { readableError } from './errors';
import {
  fetchScenarioDraft,
  listBenchmarkRollup,
  listDraftVerdicts,
} from './api';
import { DraftStatusTag } from './Drafts';
import { JobsStrip } from './Jobs';
import {
  ConflictNotice,
  JobOutcomePanel,
  JobStallNotice,
  useJobRun,
} from './JobRun';
import LlmAudit from './LlmAudit';
import MintPanel from './MintPanel';
import Timeline from './Timeline';
import {
  BenchmarkRollupRow,
  Briefing,
  BriefingItem,
  MintingRecord,
  ReviewedVerdictRow,
  ScenarioBeat,
  ScenarioDraftDetail,
  ScenarioJob,
} from './types';

const VERDICT_PAGE_SIZE = 50;

const verdictColor = (verdict: string) =>
  verdict === 'kept'
    ? 'green'
    : verdict === 'edited'
      ? 'blue'
      : verdict === 'filler'
        ? 'purple'
        : verdict === 'dropped'
          ? 'red'
          : 'default';

const impactTags = (impacts?: Record<string, number>) => {
  const entries = Object.entries(impacts || {});
  if (!entries.length)
    return <Typography.Text type="secondary">—</Typography.Text>;
  return (
    <Space size={[4, 4]} wrap className="assessment-tag-group">
      {entries.map(([code, value]) => (
        <Tag
          key={code}
          color={value > 0 ? 'green' : value < 0 ? 'red' : 'default'}
        >
          {code} {value > 0 ? `+${value}` : value}
        </Tag>
      ))}
    </Space>
  );
};

/** One draft's half of a `curation.import` plan, as the handler builds it. */
type CurationChange = {
  draft_id?: number;
  workbook?: string;
  verdicts_now?: number;
  verdicts_after?: number;
  curated_now?: boolean;
  workbook_now?: string;
  imported_at_now?: string;
  telling_items?: number;
  unmatched?: number;
  dropped?: number;
  added?: number;
  removed?: number;
  changed?: number;
  unchanged?: number;
  listed_at_most?: number;
  added_rows?: Array<Record<string, unknown>>;
  removed_rows?: Array<Record<string, unknown>>;
  changed_rows?: Array<{
    at?: string;
    fields?: string[];
    from?: Record<string, unknown>;
    to?: Record<string, unknown>;
  }>;
};

type CurationImportResult = {
  plan?: boolean;
  applied?: boolean;
  rows?: number;
  drafts?: number;
  would_change?: number;
  verdicts_written?: number;
  confirm_token?: string;
  workbooks?: string[];
  changes?: Record<string, CurationChange>;
};

/** "3 changed, 1 removed", or "nothing" when the sheet matches what is stored. */
const changeSummary = (change: CurationChange | null): string => {
  if (!change) return '';
  const parts = [
    (change.added ?? 0) > 0 ? `${change.added} added` : '',
    (change.removed ?? 0) > 0 ? `${change.removed} removed` : '',
    (change.changed ?? 0) > 0 ? `${change.changed} changed` : '',
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : 'nothing';
};

/**
 * Import a reviewed workbook onto this draft: plan first, then replace.
 *
 * The workbook is a file on the server the curators produced; the import is
 * the same service the command runs, so the screen cannot write verdicts the
 * command would have refused. It is stated as a replacement because that is
 * what it is: the rows in the sheet become the draft's curation layer, and
 * every verdict the draft holds today is deleted to make it.
 *
 * WHY TWO CLICKS. `curation.import` defaults `apply` to false, so the payload
 * this panel used to send — draft, workbook, all_drafts — ran a PLAN, wrote
 * nothing, succeeded, and was announced here as "The reviewed workbook was
 * imported" while the verdicts on screen were reloaded unchanged. A screen
 * that reports a destructive write it did not perform is worse than one that
 * refuses: the curator walks away believing the sheet is live. So the first
 * click plans and says what the replace WOULD do, and only a plan somebody
 * read can be applied — carried back by its job id and its confirm token, so
 * a sheet re-exported in between is refused rather than silently imported.
 */
const CurationImportPanel: React.FC<{
  open: boolean;
  draftId: number;
  onClose: () => void;
  onImported: () => void;
}> = ({ open, draftId, onClose, onImported }) => {
  const importJob = useJobRun();
  const [workbook, setWorkbook] = useState('');
  const [plan, setPlan] = useState<ScenarioJob | null>(null);

  useEffect(() => {
    if (!open) {
      importJob.reset();
      setPlan(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const result = (
    importJob.job?.status === 'SUCCEEDED' ? importJob.job.result : null
  ) as CurationImportResult | null;
  const applied = Boolean(result?.applied);
  const planResult = (plan?.result || null) as CurationImportResult | null;
  const change = planResult?.changes?.[String(draftId)] || null;

  const consequence = change
    ? `Applying deletes all ${change.verdicts_now ?? 0} reviewed verdict(s) on draft #${draftId} and writes ${change.verdicts_after ?? 0} from ${change.workbook || 'this workbook'}: ${changeSummary(change)}. The draft's curated telling is rebuilt from them the next time it is minted.`
    : `Importing replaces the reviewed verdicts on draft #${draftId} with the rows in this workbook. The draft's curated telling is rebuilt from them the next time it is minted.`;

  const planIt = async () => {
    setPlan(null);
    const finished = await importJob.run('curation.import', {
      draft_id: draftId,
      workbook,
      all_drafts: false,
    });
    if (finished?.status === 'SUCCEEDED') setPlan(finished);
  };

  const applyIt = async () => {
    if (!plan) return;
    const finished = await importJob.run('curation.import', {
      draft_id: draftId,
      workbook,
      all_drafts: false,
      apply: true,
      preview_job: plan.id,
      confirm_token:
        plan.confirm_token || String(planResult?.confirm_token || ''),
    });
    if (finished?.status === 'SUCCEEDED') onImported();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      title={`Import a reviewed workbook onto draft #${draftId}`}
    >
      <div className="assessment-detail-stack">
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="The plan writes nothing"
          description={`It reads the workbook and reports what replacing draft #${draftId}'s reviewed verdicts with it would add, remove and change. Nothing is deleted until you apply the plan.`}
        />
        <Input
          aria-label="Workbook path"
          placeholder="Path to the reviewed workbook on the server"
          value={workbook}
          onChange={event => {
            // A different sheet is a different plan: the token the apply
            // carries hashes the workbook's bytes, and a stale plan on screen
            // is a description of a replace nobody is about to run.
            setWorkbook(event.target.value);
            setPlan(null);
          }}
        />
        <ConflictNotice job={importJob.conflict} />
        {importJob.error && (
          <Alert
            className="assessment-callout"
            type="error"
            showIcon
            title={importJob.error}
          />
        )}
        <JobOutcomePanel
          job={importJob.job}
          successTitle={
            applied
              ? 'The reviewed verdicts were replaced'
              : 'Plan ready — nothing has been written'
          }
          elapsedMs={importJob.elapsedMs}
        />
        <JobStallNotice stall={importJob.stall} />

        {change && !applied && (
          <Card size="small" title="What applying this plan would do">
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Verdicts">
                {`${change.verdicts_now ?? 0} → ${change.verdicts_after ?? 0}`}
              </Descriptions.Item>
              <Descriptions.Item label="Rows that move">
                {changeSummary(change)}
              </Descriptions.Item>
              <Descriptions.Item label="Unchanged">
                {change.unchanged ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="Sheet rows the telling has no place for">
                {change.unmatched ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="Curated today by" span={2}>
                {change.curated_now
                  ? `${change.workbook_now || 'an unnamed workbook'}${
                      change.imported_at_now
                        ? `, imported ${formatDateTime(change.imported_at_now)}`
                        : ''
                    }`
                  : 'This draft has never been curated.'}
              </Descriptions.Item>
            </Descriptions>
            {(change.changed_rows || []).length > 0 && (
              <Table
                rowKey={(_, index) => String(index)}
                className="assessment-table"
                size="small"
                tableLayout="fixed"
                pagination={false}
                dataSource={change.changed_rows}
                columns={[
                  { title: 'At', dataIndex: 'at', width: 160 },
                  {
                    title: 'What moves',
                    dataIndex: 'fields',
                    width: 180,
                    render: (value: unknown) =>
                      Array.isArray(value) ? value.join(', ') : '—',
                  },
                  {
                    title: 'To',
                    dataIndex: 'to',
                    render: (value: unknown) => (
                      <EllipsisText
                        value={String(
                          (value as { headline?: string })?.headline ||
                            JSON.stringify(value ?? null),
                        )}
                      />
                    ),
                  },
                ]}
              />
            )}
            {(change.removed_rows || []).length > 0 && (
              <Typography.Paragraph>
                <Typography.Text type="secondary">
                  {`${change.removed} verdict(s) are deleted and not replaced; at most ${change.listed_at_most ?? 25} of each kind are listed.`}
                </Typography.Text>
              </Typography.Paragraph>
            )}
          </Card>
        )}

        {!plan || applied ? (
          // No confirmation on a dry run: a click that writes nothing should
          // not be guarded like one that deletes fourteen verdicts, or the
          // guard on the one that does stops being read.
          <Button
            type="primary"
            disabled={!workbook.trim()}
            loading={importJob.running}
            onClick={planIt}
          >
            Plan the import
          </Button>
        ) : (
          <Popconfirm
            title="Replace this draft's reviewed verdicts?"
            description={consequence}
            okText="Yes, replace them"
            cancelText="Not yet"
            onConfirm={applyIt}
          >
            <Button type="primary" danger loading={importJob.running}>
              Replace the verdicts
            </Button>
          </Popconfirm>
        )}
      </div>
    </Modal>
  );
};

type Props = {
  draftId: number;
  onBack: () => void;
};

const DraftDetail: React.FC<Props> = ({ draftId, onBack }) => {
  const [detail, setDetail] = useState<ScenarioDraftDetail | null>(null);
  const [rollup, setRollup] = useState<BenchmarkRollupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [verdicts, setVerdicts] = useState<ReviewedVerdictRow[]>([]);
  const [verdictCount, setVerdictCount] = useState(0);
  const [verdictPage, setVerdictPage] = useState(1);
  const [verdictTarget, setVerdictTarget] = useState<string | undefined>('pm');
  const [verdictFilter, setVerdictFilter] = useState<string | undefined>();
  const [verdictsLoading, setVerdictsLoading] = useState(false);
  const [verdictError, setVerdictError] = useState('');

  const [mintOpen, setMintOpen] = useState(false);
  const [curationOpen, setCurationOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setDetail(await fetchScenarioDraft(draftId));
    } catch (requestError) {
      setError(readableError(requestError, { what: 'That draft' }));
    } finally {
      setLoading(false);
    }
    // The rollup only decorates minting records with assessment links, so a
    // failure here degrades to missing links rather than sinking the page.
    try {
      setRollup(await listBenchmarkRollup());
    } catch (_rollupError) {
      setRollup([]);
    }
  }, [draftId]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadVerdicts = useCallback(async () => {
    setVerdictsLoading(true);
    setVerdictError('');
    try {
      const response = await listDraftVerdicts(draftId, {
        target: verdictTarget,
        verdict: verdictFilter,
        page: verdictPage,
        page_size: VERDICT_PAGE_SIZE,
      });
      setVerdicts(response.results);
      setVerdictCount(response.pagination.count);
    } catch (requestError) {
      setVerdictError(
        readableError(requestError, { what: "This draft's reviewed rows" }),
      );
    } finally {
      setVerdictsLoading(false);
    }
  }, [draftId, verdictTarget, verdictFilter, verdictPage]);

  useEffect(() => {
    void loadVerdicts();
  }, [loadVerdicts]);

  if (loading && !detail) {
    return (
      <div className="assessment-loading">
        <Spin size="large" tip="Loading scenario draft" />
      </div>
    );
  }

  if (!detail) {
    return (
      <Result
        status="error"
        title="Scenario draft could not be loaded"
        subTitle={error}
        extra={<Button onClick={onBack}>Back to drafts</Button>}
      />
    );
  }

  const beats: ScenarioBeat[] = Array.isArray(detail.canonical?.beats)
    ? (detail.canonical.beats as ScenarioBeat[])
    : [];
  const briefings: Briefing[] = detail.briefings?.length
    ? detail.briefings
    : ((detail.canonical?.briefings as Briefing[]) ?? []);
  const briefingReport =
    detail.briefing_report ??
    (detail.canonical?.briefing_report as
      | ScenarioDraftDetail['briefing_report']
      | undefined) ??
    null;
  const mintings: MintingRecord[] = detail.mintings || [];
  // The blueprint the draft is served by, as the detail endpoint already
  // resolves it: the (version, template session) coordinate every screen
  // downstream names. The answer key is derived from that blueprint's own
  // page, not from here. A draft served by two families answers with an
  // error instead of a guess.
  const binding = (
    detail as ScenarioDraftDetail & {
      blueprint?: DraftBlueprintBinding | null;
    }
  ).blueprint;

  // Assessment link per blueprint family: the rollup row that names the
  // blueprint a mint produced carries the scenario code the Assessments
  // admin is keyed by.
  const scenarioForBlueprint = (blueprintId?: number) =>
    blueprintId == null
      ? undefined
      : rollup.find(row => row.blueprint?.id === blueprintId);

  const overview = (
    <div className="assessment-detail-stack">
      <Card size="small" title="Recent jobs on this draft">
        <JobsStrip target_kind="scenario_draft" target_id={detail.id} />
      </Card>
      <Card size="small" title="Draft identity">
        <Descriptions bordered size="small" column={3}>
          <Descriptions.Item label="Draft id">
            <Typography.Text code>#{detail.id}</Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <DraftStatusTag status={detail.status} />
          </Descriptions.Item>
          <Descriptions.Item label="Window">
            {detail.window_start} – {detail.window_end}
          </Descriptions.Item>
          <Descriptions.Item label="Session duration">
            {detail.session_duration_s != null
              ? `${detail.session_duration_s}s`
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Start state">
            <Tag
              color={detail.start_state === 'inherited' ? 'purple' : 'default'}
            >
              {(detail.start_state || 'flat').toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Outcome code">
            {detail.outcome_code ? (
              <Typography.Text code>{detail.outcome_code}</Typography.Text>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Agent run">
            {detail.agent_run_id != null ? `#${detail.agent_run_id}` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Captured">
            {formatDateTime(detail.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label="Beat events">
            {detail.event_ids?.length ?? 0}
          </Descriptions.Item>
          <Descriptions.Item label="Serving blueprint" span={3}>
            {binding?.error ? (
              <Typography.Text type="danger">{binding.error}</Typography.Text>
            ) : binding ? (
              <Space size={[4, 4]} wrap className="assessment-tag-group">
                {/* The family's NAME is already this page's title; repeating
                    it here would say nothing. What the reader needs is the
                    version and template session a mint or a score lands on. */}
                <Link to={`/dashboard/blueprints/${binding.blueprint_id}`}>
                  Blueprint #{binding.blueprint_id}
                </Link>
                <Tag>v{binding.version_number}</Tag>
                <Tag>{binding.version_status_label}</Tag>
                <Tag>template session #{binding.template_session_id}</Tag>
              </Space>
            ) : (
              <Typography.Text type="secondary">
                No blueprint family carries this draft's tag yet.
              </Typography.Text>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <Card size="small" title="Learning outcome">
        <Typography.Paragraph>
          {detail.learning_outcome || (
            <Typography.Text type="secondary">
              No learning outcome recorded.
            </Typography.Text>
          )}
        </Typography.Paragraph>
      </Card>
      <Card size="small" title="Narrative">
        <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>
          {detail.narrative || (
            <Typography.Text type="secondary">
              No narrative recorded.
            </Typography.Text>
          )}
        </Typography.Paragraph>
      </Card>
      <Card size="small" title="Macro background">
        <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>
          {detail.macro_background || (
            <Typography.Text type="secondary">
              No macro background recorded.
            </Typography.Text>
          )}
        </Typography.Paragraph>
      </Card>
      <Card size="small" title="Opening book">
        {detail.opening_book?.length ? (
          <>
            <JsonBlock value={detail.opening_book} />
            {detail.opening_book_rationale && (
              <Typography.Paragraph style={{ marginTop: 12 }}>
                {detail.opening_book_rationale}
              </Typography.Paragraph>
            )}
          </>
        ) : (
          <Typography.Text type="secondary">
            Flat start — the trainee builds a position from scratch.
          </Typography.Text>
        )}
      </Card>
      <Card
        size="small"
        title="Curation"
        extra={
          <Button size="small" onClick={() => setCurationOpen(true)}>
            Import reviewed workbook
          </Button>
        }
      >
        {detail.curation ? (
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Workbook">
              <EllipsisText code value={detail.curation.workbook} />
            </Descriptions.Item>
            <Descriptions.Item label="Imported">
              {formatDateTime(detail.curation.imported_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Rows">
              {detail.curation.rows}
            </Descriptions.Item>
            <Descriptions.Item label="Verdicts">
              <Space size={[4, 4]} wrap className="assessment-tag-group">
                {Object.entries(detail.curation.verdicts || {}).map(
                  ([verdict, count]) => (
                    <Tag key={verdict} color={verdictColor(verdict)}>
                      {verdict.toUpperCase()} {count}
                    </Tag>
                  ),
                )}
              </Space>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Typography.Text type="secondary">
            Uncurated — no reviewed workbook has been imported for this draft.
          </Typography.Text>
        )}
      </Card>
    </div>
  );

  const beatColumns: any[] = [
    {
      title: 'Time',
      dataIndex: 'time',
      width: 170,
      className: 'assessment-cell-nowrap',
      render: (value: string) => formatDateTime(value),
    },
    {
      title: 'Event',
      dataIndex: 'event_id',
      width: 100,
      render: (value: number | undefined) =>
        value != null ? <Typography.Text code>#{value}</Typography.Text> : '—',
    },
    {
      title: 'Headline',
      dataIndex: 'headline',
      render: (value: string) => value || '—',
    },
    {
      title: 'Impacts',
      dataIndex: 'impacts',
      width: 260,
      render: (value: Record<string, number> | undefined) => impactTags(value),
    },
    {
      title: 'Rationale',
      dataIndex: 'rationale',
      width: 260,
      render: (value: string) =>
        value || <Typography.Text type="secondary">—</Typography.Text>,
    },
  ];

  const beatsTab = (
    <Card size="small" title={`Beats (${beats.length})`}>
      <Table
        rowKey={(_, index) => String(index)}
        className="assessment-table"
        tableLayout="fixed"
        dataSource={beats}
        columns={beatColumns}
        scroll={{ x: 1100 }}
        pagination={false}
      />
    </Card>
  );

  const briefingItemColumns: any[] = [
    {
      title: 'Time',
      dataIndex: 'time',
      width: 170,
      className: 'assessment-cell-nowrap',
      render: (value: string | undefined) => formatDateTime(value),
    },
    {
      title: 'Kind',
      dataIndex: 'kind',
      width: 110,
      render: (value: string | undefined) => (
        <Tag color={value === 'synthetic' ? 'purple' : 'blue'}>
          {(value || 'real').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Headline',
      dataIndex: 'headline',
      render: (value: string) => value || '—',
    },
    {
      title: 'Instruments',
      dataIndex: 'instruments',
      width: 220,
      render: (value: string[] | undefined) =>
        value?.length ? (
          <Space size={[4, 4]} wrap className="assessment-tag-group">
            {value.map(code => (
              <Tag key={code}>{code}</Tag>
            ))}
          </Space>
        ) : (
          '—'
        ),
    },
    {
      title: 'Sources',
      width: 160,
      render: (_: unknown, item: BriefingItem) =>
        item.event_id != null ? (
          <Typography.Text code>#{item.event_id}</Typography.Text>
        ) : item.source_event_ids?.length ? (
          <EllipsisText
            code
            value={item.source_event_ids.map(id => `#${id}`).join(' ')}
          />
        ) : (
          '—'
        ),
    },
  ];

  const briefingsTab = (
    <div className="assessment-detail-stack">
      {briefingReport && (
        <Card size="small" title="Briefing report">
          <JsonBlock compact value={briefingReport} />
        </Card>
      )}
      {briefings.length === 0 && (
        <Alert
          showIcon
          type="info"
          title="No briefings"
          description="This draft carries no trainee briefing layer. Briefings are written by the enrich_briefings command after capture."
        />
      )}
      {briefings.map(bucket => (
        <Card
          key={bucket.period}
          size="small"
          title={
            <Space>
              <Typography.Text code>{bucket.period}</Typography.Text>
              {bucket.start && bucket.end && (
                <Typography.Text type="secondary">
                  {bucket.start} – {bucket.end}
                </Typography.Text>
              )}
            </Space>
          }
        >
          {bucket.note && (
            <Typography.Paragraph type="secondary">
              {bucket.note}
            </Typography.Paragraph>
          )}
          <Table
            rowKey={(_, index) => String(index)}
            className="assessment-table"
            tableLayout="fixed"
            dataSource={bucket.items || []}
            columns={briefingItemColumns}
            scroll={{ x: 1000 }}
            pagination={false}
          />
        </Card>
      ))}
    </div>
  );

  const verdictColumns: any[] = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      width: 170,
      className: 'assessment-cell-nowrap',
      render: (value: string) => formatDateTime(value),
    },
    {
      title: 'Verdict',
      dataIndex: 'verdict',
      width: 100,
      render: (value: string) => (
        <Tag color={verdictColor(value)}>{value.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Headline',
      render: (_: unknown, row: ReviewedVerdictRow) => (
        <Space direction="vertical" size={0} style={{ maxWidth: '100%' }}>
          <span>{row.headline || '—'}</span>
          {row.original_headline && row.original_headline !== row.headline && (
            <Typography.Text delete type="secondary">
              {row.original_headline}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Why changed',
      dataIndex: 'why_changed',
      width: 240,
      render: (value: string) =>
        value || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Kind',
      dataIndex: 'kind',
      width: 100,
      render: (value: string) =>
        value ? <Tag>{value.toUpperCase()}</Tag> : '—',
    },
    {
      title: 'Matched',
      dataIndex: 'matched_current',
      width: 100,
      render: (value: boolean) => (
        <Tag color={value ? 'green' : 'orange'}>{value ? 'YES' : 'NO'}</Tag>
      ),
    },
    {
      title: 'Workbook',
      dataIndex: 'source_workbook',
      width: 200,
      render: (value: string) => <EllipsisText code value={value} />,
    },
  ];

  const verdictsTab = (
    <Card
      size="small"
      title="Reviewed news verdicts"
      extra={
        <Button
          icon={<ReloadOutlined />}
          loading={verdictsLoading}
          onClick={loadVerdicts}
        >
          Refresh
        </Button>
      }
    >
      {detail.verdict_summary?.length ? (
        <Space className="assessment-filter-row" size={[4, 4]} wrap>
          {detail.verdict_summary.map(row => (
            <Tag
              key={`${row.target}-${row.verdict}`}
              color={verdictColor(row.verdict)}
            >
              {row.target.toUpperCase()} · {row.verdict.toUpperCase()}{' '}
              {row.count}
            </Tag>
          ))}
        </Space>
      ) : null}
      <Space className="assessment-filter-row" wrap>
        <Select
          allowClear
          aria-label="Verdict target"
          placeholder="All targets"
          style={{ width: 160 }}
          value={verdictTarget}
          onChange={value => {
            setVerdictTarget(value);
            setVerdictPage(1);
          }}
          options={[
            { value: 'pm', label: 'pm (blueprint)' },
            { value: 'sim1', label: 'sim1' },
            { value: 'sim20', label: 'Sim2.0' },
          ]}
        />
        <Select
          allowClear
          aria-label="Verdict filter"
          placeholder="All verdicts"
          style={{ width: 160 }}
          value={verdictFilter}
          onChange={value => {
            setVerdictFilter(value);
            setVerdictPage(1);
          }}
          options={[
            { value: 'kept', label: 'Kept' },
            { value: 'edited', label: 'Edited' },
            { value: 'filler', label: 'Filler' },
            { value: 'dropped', label: 'Dropped' },
          ]}
        />
      </Space>
      {verdictError && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={verdictError}
        />
      )}
      <Table
        rowKey="id"
        className="assessment-table"
        tableLayout="fixed"
        loading={verdictsLoading}
        dataSource={verdicts}
        columns={verdictColumns}
        scroll={{ x: 1300 }}
        pagination={{
          current: verdictPage,
          pageSize: VERDICT_PAGE_SIZE,
          total: verdictCount,
          showSizeChanger: false,
          onChange: setVerdictPage,
        }}
      />
    </Card>
  );

  const mintingsTab = (
    <div className="assessment-detail-stack">
      {mintings.length === 0 && (
        <Alert
          showIcon
          type="info"
          title="No recorded mint"
          description="No minting records exist on this draft. A family minted by legacy NewsRag before the migration carries no records; a refresh of such a family requires force on the mint command."
        />
      )}
      {[...mintings].reverse().map((record, reversedIndex) => {
        const position = mintings.length - reversedIndex;
        const scenario = scenarioForBlueprint(record.blueprint_id);
        const importSummary = record.import || {};
        const result =
          importSummary.created_family === true
            ? 'NEW FAMILY'
            : importSummary.refreshed_in_place === true
              ? 'REFRESHED'
              : null;
        return (
          <Card
            key={`${record.minted_at || position}`}
            size="small"
            title={
              <Space>
                <span>
                  Mint {position} of {mintings.length}
                </span>
                {result && (
                  <Tag color={result === 'NEW FAMILY' ? 'blue' : 'purple'}>
                    {result}
                  </Tag>
                )}
                {record.pm_edits_overwritten && (
                  <Tag color="red">PM EDITS OVERWRITTEN</Tag>
                )}
              </Space>
            }
            extra={
              <Space>
                {record.blueprint_id != null && (
                  <Link to={`/dashboard/blueprints/${record.blueprint_id}`}>
                    <Button size="small">Open blueprint</Button>
                  </Link>
                )}
                {scenario && (
                  <Link
                    to={`/dashboard/assessments?scenario=${encodeURIComponent(
                      scenario.code,
                    )}`}
                  >
                    <Button size="small">Open in Assessments admin</Button>
                  </Link>
                )}
              </Space>
            }
          >
            <Descriptions bordered size="small" column={3}>
              <Descriptions.Item label="Minted at">
                {formatDateTime(record.minted_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Minted by">
                {/* The record stores an id; the read resolves the name. "#33"
                    named nobody — this product has no user directory, so an
                    admin who read an id there had nowhere to look it up. */}
                {record.minted_by_username ||
                  (record.minted_by != null
                    ? `A user who no longer exists (id ${record.minted_by})`
                    : '—')}
              </Descriptions.Item>
              <Descriptions.Item label="Copy">
                <Tag color={record.curated ? 'green' : 'default'}>
                  {record.curated ? 'CURATED' : `MODEL ${record.model || '?'}`}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Blueprint">
                {record.blueprint_id != null ? `#${record.blueprint_id}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Version">
                {record.version != null
                  ? `v${record.version} (id ${record.blueprint_version_id ?? '?'})`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Template session">
                {record.template_session_id != null
                  ? `#${record.template_session_id}`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Tag">
                <EllipsisText code value={record.tag} />
              </Descriptions.Item>
              <Descriptions.Item label="Serving tag">
                <EllipsisText code value={record.serving_tag} />
              </Descriptions.Item>
              <Descriptions.Item label="PM edit fingerprint">
                <EllipsisText code value={record.pm_fingerprint} />
              </Descriptions.Item>
            </Descriptions>
            <Card
              className="assessment-inner-card"
              size="small"
              title="Import summary"
            >
              <JsonBlock compact value={importSummary} />
            </Card>
          </Card>
        );
      })}
    </div>
  );

  const canonicalTab = (
    <Card size="small" title="Canonical draft (validator output, verbatim)">
      <JsonBlock value={detail.canonical} />
    </Card>
  );

  const llmTab = (
    <LlmAudit
      title={`Model calls about draft #${detail.id}`}
      filters={{ ref_id: detail.id }}
      refTypeOptions={[
        { value: 'scenario_rendition', label: 'Retelling (the mint)' },
        { value: 'scenario_draft', label: 'Briefings and macro background' },
      ]}
      approximate="A call written before the job ledger existed carries no job id, so these are the calls about this draft, not the calls of one mint."
    />
  );

  return (
    <div>
      <div className="assessment-workspace-header">
        <Space size={16}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            Back to drafts
          </Button>
          <Typography.Title className="assessment-title" level={3}>
            {detail.name}
          </Typography.Title>
          <DraftStatusTag status={detail.status} />
          <Typography.Text type="secondary">
            {detail.window_start} – {detail.window_end}
          </Typography.Text>
        </Space>
        <Space>
          <Button onClick={() => setTimelineOpen(true)}>Timeline</Button>
          <Button
            type="primary"
            icon={<ExperimentOutlined />}
            disabled={detail.status === 'archived'}
            onClick={() => setMintOpen(true)}
          >
            Mint
          </Button>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
            Refresh
          </Button>
        </Space>
      </div>
      {detail.status === 'archived' && (
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="An archived draft is never minted"
          description="Restore its lifecycle state first; the mint refuses an archived draft whatever else is true of it."
        />
      )}
      {error && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={error}
        />
      )}
      <Tabs
        items={[
          { key: 'overview', label: 'Overview', children: overview },
          {
            key: 'beats',
            label: `Beats (${beats.length})`,
            children: beatsTab,
          },
          {
            key: 'briefings',
            label: `Briefings (${briefings.length})`,
            children: briefingsTab,
          },
          {
            key: 'verdicts',
            label: `Verdicts (${detail.verdict_count})`,
            children: verdictsTab,
          },
          {
            key: 'mintings',
            label: `Mintings (${mintings.length})`,
            children: mintingsTab,
          },
          { key: 'canonical', label: 'Canonical', children: canonicalTab },
          { key: 'llm-calls', label: 'Model calls', children: llmTab },
        ]}
      />
      <MintPanel
        open={mintOpen}
        draftId={detail.id}
        draftName={detail.name}
        curated={detail.curated}
        onClose={() => setMintOpen(false)}
        onApplied={() => {
          // The mintings tab, the serving blueprint and the coordinates all
          // come from the row the mint just appended to.
          void load();
        }}
      />
      <CurationImportPanel
        open={curationOpen}
        draftId={detail.id}
        onClose={() => setCurationOpen(false)}
        onImported={() => {
          void load();
          void loadVerdicts();
        }}
      />
      <Timeline
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
        draft={detail.id}
        title={`Timeline — ${detail.name}`}
      />
    </div>
  );
};

export default DraftDetail;
